import { FunctionComponent, useEffect } from 'react';
import { usePlayground, usePlaygroundMutations } from '@playground/context/hooks';
import RunTestsBtn from '@playground/components/RunTestsBtn';
import Lives from '@playground/components/Lives';
import MonacoEditor from '@playground/components/MonacoEditor';
import Status from '@playground/components/Status';
import { useActiveCode } from '@playground/SQL/hooks/useActiveCode';
import { usePyodide } from '@playground/SQL/hooks/usePyodide';
import { saveExerciseInLocal } from '@services/challenge';
import RunBtn from '@playground/components/RunBtn';
import FileTabs from '@playground/components/FileTabs';
import confetti from 'canvas-confetti';

const CodeEditor: FunctionComponent = () => {
  const { runner, exercise } = usePlayground();
  const { setRunner } = usePlaygroundMutations();
  const { code, updateCode, activeFile } = useActiveCode();
  const { pyodide } = usePyodide();

  const runTests = () => {
    if (runner.currentTabIndex !== 2) {
      setRunner({ currentTabIndex: 2 });
    }
    pyodide.runTests();
  };

  const runCode = () => {
    if (runner.currentTabIndex !== 1) {
      setRunner({ currentTabIndex: 1 });
    }
    pyodide.runCode();
  };

  useEffect(() => {
    if (pyodide.tests?.status === 'fail' && runner.lives > 0) {
      setRunner({ lives: runner.lives - 1 });
    } else if (pyodide.tests?.status === 'success') {
      saveExerciseInLocal({
        challengeId: exercise?.pathId ?? '',
        exerciseId: exercise?.id ?? '',
        files: { ...pyodide.files },
      });
      confetti({
        zIndex: 999,
        particleCount: 200,
        spread: 90,
        origin: { x: 0.5, y: 0.6 },
      });
    }
  }, [pyodide.tests]);

  return (
    <div className="flex flex-col grow w-full p-2 shadow-lg ring-1 ring-black/5 items-center highlight-white/5 rounded-lg relative bg-[#011627]">
      <FileTabs
        activeFile={pyodide.activeFile}
        visibleFiles={pyodide.getVisibleFiles()}
        setActiveFile={(file) => pyodide.setActiveFile(file)}
      />
      <div className="grow w-full">
        <div className="flex h-96 md:h-full pt-3">
          <MonacoEditor keyFile={activeFile} code={code} updateCode={updateCode} />
          <Status status={pyodide.status} />
        </div>
      </div>
      <div className="flex border-t border-zinc-400 w-full flex-col md:flex-row md:justify-between p-4">
        <Lives
          lives={runner.lives}
          showSolution={() => {
            pyodide.setFiles(exercise.filesSolution);
          }}
        />
        <div className="flex space-x-2">
          {pyodide.status === 'idle' && (
            <>
              <RunBtn run={runCode} />
              <RunTestsBtn testsStatus={runner.testsStatus} runTests={runTests} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CodeEditor;
